import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookMarked, Lock, MapPin } from "lucide-react";
import api from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";

function CountryCard({ c }) {
  return (
    <div className="rounded-2xl border-2 p-3 flex items-center gap-3" data-testid={`passport-${c.code}`}
      style={{ background: c.collected ? "var(--t-surface)" : "var(--t-surface2)", borderColor: c.collected ? "var(--t-accent)" : "var(--t-border)", opacity: c.collected ? 1 : 0.6 }}>
      <div className="w-12 h-9 rounded-md overflow-hidden shrink-0 border" style={{ borderColor: "var(--t-border)" }}>
        {c.collected ? (
          <img src={`https://flagcdn.com/w80/${c.code}.png`} alt={c.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: "var(--t-border)" }}><Lock className="w-3.5 h-3.5" style={{ color: "var(--t-muted)" }} /></div>
        )}
      </div>
      <div className="min-w-0">
        <p className="font-heading font-extrabold text-sm truncate">{c.collected ? c.name : "???"}</p>
        <p className="text-xs font-semibold flex items-center gap-1 truncate" style={{ color: "var(--t-muted)" }}>
          <MapPin className="w-3 h-3 shrink-0" /> {c.collected ? c.capital : "Nog ontdekken"}
        </p>
      </div>
    </div>
  );
}

export default function Passport() {
  const navigate = useNavigate();
  const { terms } = useTheme();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/child/world/passport").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const muted = { color: "var(--t-muted)" };
  const wkCollected = data ? data.wk.filter((c) => c.collected).length : 0;

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 85%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/leerling/wereldreis")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="passport-back-btn"><ArrowLeft className="w-5 h-5 mr-1" /> Wereldreis</Button>
          {data && <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "var(--t-surface2)" }} data-testid="passport-total"><BookMarked className="w-4 h-4" style={{ color: "var(--t-accent)" }} /><span className="font-heading font-extrabold">{data.collected_count}/{data.total_count}</span></div>}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-8 pb-16">
        <div className="flex items-center gap-4 mb-8">
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 3 }} className="w-16 h-16 rounded-3xl flex items-center justify-center text-4xl" style={{ background: "var(--t-surface2)" }}>📔</motion.div>
          <div>
            <h1 className="font-heading text-3xl font-black themed-heading">Wereldpaspoort</h1>
            <p className="font-semibold" style={muted}>Elk land dat je goed plaatst of raadt, komt in je paspoort.</p>
          </div>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div>
        ) : (
          <>
            {/* WK-poule */}
            <ThemedCard className="p-5 mb-8" data-testid="passport-wk-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-extrabold themed-heading">🏆 WK-poule</h2>
                <span className="font-bold text-sm" style={{ color: "var(--t-accent)" }}>{wkCollected}/{data.wk.length}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {data.wk.map((c) => <CountryCard key={c.code} c={c} />)}
              </div>
            </ThemedCard>

            {/* Per werelddeel */}
            {data.groups.map((g) => {
              const collected = g.countries.filter((c) => c.collected).length;
              return (
                <div key={g.continent} className="mb-8" data-testid={`passport-group-${g.continent}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading text-xl font-extrabold themed-heading">{g.continent}</h2>
                    <span className="font-bold text-sm" style={muted}>{collected}/{g.countries.length}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {g.countries.map((c) => <CountryCard key={c.code} c={c} />)}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </main>
    </ThemedShell>
  );
}
